import { useMemo } from "react";

import { Collapsible, Small } from "@fronterahealth/frontera-ui-components";

import { ApiLearnerFileFileKindChoices, LearnerFileType } from "@api/graphql/types-and-hooks";
import { AssessmentFile } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentFile";

// Document type categories for grouping
type DocumentCategory = "Interviews" | "Intake" | "Assessments" | "Supporting Documents" | "Behavioral Observations";

const FILE_KIND_TO_CATEGORY: Record<string, DocumentCategory> = {
  [ApiLearnerFileFileKindChoices.Interview]: "Interviews",
  [ApiLearnerFileFileKindChoices.AdiRInterviewNotes]: "Assessments",
  [ApiLearnerFileFileKindChoices.BehavioralObservations]: "Behavioral Observations",
  [ApiLearnerFileFileKindChoices.ClinicalNotes]: "Supporting Documents",
  [ApiLearnerFileFileKindChoices.DiagnosisReport]: "Supporting Documents",
  [ApiLearnerFileFileKindChoices.IntakeCall]: "Interviews",
  [ApiLearnerFileFileKindChoices.ActionBehavioralCenterIntake]: "Intake",
  [ApiLearnerFileFileKindChoices.Unclassified]: "Supporting Documents",
};

// All assessment types default to "Assessments" category
const isAssessmentType = (fileKind: string): boolean => {
  return !FILE_KIND_TO_CATEGORY[fileKind];
};

const getFileCategory = (fileKind: string): DocumentCategory => {
  if (isAssessmentType(fileKind)) {
    return "Assessments";
  }
  return FILE_KIND_TO_CATEGORY[fileKind] || "Supporting Documents";
};

// Order for displaying categories
const CATEGORY_ORDER: DocumentCategory[] = [
  "Interviews",
  "Intake",
  "Assessments",
  "Behavioral Observations",
  "Supporting Documents",
];

interface FilesCollapsibleProps {
  headerText?: string;
  headerIcon?: React.ReactNode;
  topContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  files: LearnerFileType[];
  downloadFileCallback?: (fileId: string) => void;
  removeFileCallback?: (fileId: string) => void;
  fileClickedCallback?: (file: LearnerFileType) => void;
  groupByDocumentType?: boolean;
}

export const FilesCollapsible: React.FC<FilesCollapsibleProps> = ({
  files,
  headerText,
  headerIcon,
  topContent,
  bottomContent,
  downloadFileCallback,
  removeFileCallback,
  fileClickedCallback,
  groupByDocumentType = false,
}) => {
  // Group files by category
  const groupedFiles = useMemo(() => {
    if (!groupByDocumentType) return null;

    const groups: Record<DocumentCategory, LearnerFileType[]> = {
      Interviews: [],
      Intake: [],
      Assessments: [],
      "Behavioral Observations": [],
      "Supporting Documents": [],
    };

    files.forEach((file) => {
      if (file) {
        const category = getFileCategory(file.fileKind);
        groups[category].push(file);
      }
    });

    return groups;
  }, [files, groupByDocumentType]);

  const renderFiles = () => {
    if (groupByDocumentType && groupedFiles) {
      return (
        <div className="mt-2 flex flex-col gap-3 w-full">
          {CATEGORY_ORDER.map((category) => {
            const categoryFiles = groupedFiles[category];
            if (categoryFiles.length === 0) return null;

            return (
              <div key={category}>
                <Small displayType="loud" colorType="primary" className="text-xs mb-1">
                  {category}
                </Small>
                <div className="flex flex-col gap-1">
                  {categoryFiles.map((file) => (
                    <AssessmentFile
                      key={file.id}
                      file={file}
                      downloadCallback={downloadFileCallback}
                      removeCallback={removeFileCallback}
                      fileClickedCallback={fileClickedCallback}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="mt-2 flex flex-col gap-1 w-full">
        {files.map((file) => {
          return file ? (
            <AssessmentFile
              key={file.id}
              file={file}
              downloadCallback={downloadFileCallback}
              removeCallback={removeFileCallback}
              fileClickedCallback={fileClickedCallback}
            />
          ) : null;
        })}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl py-1 pl-4 pr-0 border-limestone-100 border w-full h-full flex flex-col [&>div>button>svg]:hidden">
      <Collapsible
        defaultOpenState={true}
        headerContent={
          <div className="flex items-center pb-2">
            {headerIcon}
            <Small displayType="loud" colorType="primary" className="text-sm ml-1">
              {headerText}
            </Small>
          </div>
        }
        headerIsClickable={false}
        bodyContent={
          <div className="pr-4 flex flex-col max-h-[35vh]">
            <div className="flex-1 overflow-y-auto min-h-0">
              {topContent}
              {renderFiles()}
            </div>
            {bottomContent && <div className="shrink-0 bg-white pb-1">{bottomContent}</div>}
          </div>
        }
      />
    </div>
  );
};
